import {forwardRef, useEffect, useState} from 'react'
import Box from '@mui/material/Box'

import TabPanel from '@mui/lab/TabPanel'
import {StyledTabList, StyledTab, StyledGrid, StyledTabBox, StyledSnackbar, StyledAlertSnackbar} from './Styles'
import {useDispatch, useSelector} from 'react-redux'
import Segment from '../../../configs/prospectsSegmentTabList.json'
import {selectedTab} from '../../../store/prospectSegment/selectedProspectsTab/selectedProspectsTabSlice'
import {Grid} from '@mui/material'
import {useResizeDetector} from 'react-resize-detector'
import {useSaveUserPreferences} from '../../../api/addColumnPreferences'
import {
  updateTablePageNumber,
  updateTableData,
  dispatchSearchTerm,
  selectSearchTerm
} from '../../Common/MappingHelperConst'
import {constituentIdList} from '../../../store/prospectSegment/savedProspectsList/saveProspectsListSlice'
import {
  setSortConfig,
  setSortColumn,
  setSortDirection
} from '../../../store/prospectSegment/allProspectsData/allProspectsDataSlice'
import {TabContext} from '@mui/lab'
import PropTypes from 'prop-types'
import AllProspects from './AllProspects'
import SavedProspects from './SavedProspects'
import {clearExportError, hideExportingInfo} from '../../../store/prospectSegment/exportList/exportListSlice'

const componentMapping = [
  {contentKey: Segment.segments.allProspects.key, content: AllProspects},
  {contentKey: Segment.segments.savedProspects.key, content: SavedProspects}
]

const CustomTabPanel = forwardRef((props, ref) => {
  const {value} = props
  const selectedItem = componentMapping.find((item) => item.contentKey === value)
  const Component = selectedItem.content
  const {height, ref: containerRef} = useResizeDetector()

  return (
    <TabPanel value={value} ref={containerRef}>
      <Component height={height} />
    </TabPanel>
  )
})
CustomTabPanel.displayName = 'CustomTabPanel'

const ProspectSegmentation = () => {
  const totalCount = useSelector((state) => state.totalProspectsCount.value)
  const dispatch = useDispatch()
  const value = useSelector((state) => state.selectedProspectTab.value)
  const currentSelectedTab = useSelector((state) => state.selectedProspectTab.value)
  const dispatchSearchTerms = dispatchSearchTerm.find((item) => item.contentKey === currentSelectedTab)
  const searchTermSelector = selectSearchTerm.find((item) => item.contentKey === currentSelectedTab)
  const searchString = useSelector((state) => state.search[searchTermSelector.selectorContent])
  const {currentTerm, previousTerm} = searchString
  const handleChange = (event, newValue) => {
    const getSelectedTabData = updateTableData.find((item) => item.contentKey !== newValue)
    const updateSelectedTabPageNumber = updateTablePageNumber.find((item) => item.contentKey !== newValue)
    if (currentTerm !== previousTerm) {
      dispatch(getSelectedTabData.selectorContent([]))
      dispatch(updateSelectedTabPageNumber.selectorContent(1))
      dispatch(dispatchSearchTerms.selectorContent({currentTerm: '', previousTerm: ''}))
    }
    dispatch(constituentIdList([]))
    dispatch(setSortConfig({direction: 'asc', key: null}))
    dispatch(setSortColumn(null))
    dispatch(setSortDirection('asc'))
    dispatch(selectedTab(newValue))
  }

  const [userColumnPreferences, {data, loading, error}] = useSaveUserPreferences()
  const updatedHeaders = useSelector((state) => state.addColumnProspectsTable.defaultValue)
  const getCampaignAction = useSelector((state) => state.campaignAction)
  const {actionName} = getCampaignAction
  const azureRole = useSelector((state) => state.superUserTenantSlice.azureRole)

  useEffect(() => {
    if (actionName === 'saveUserColumnPreference') {
      setOpenAlert(false)
      userColumnPreferences({
        variables: {
          columns: updatedHeaders,
          ...(azureRole ? {azureRole} : {})
        }
      })
    }
  }, [actionName, updatedHeaders, userColumnPreferences, azureRole])

  const [openAlert, setOpenAlert] = useState(false)
  const exportError = useSelector((state) => state.exportList)

  const handleAlertClose = () => {
    setOpenAlert(false)
  }

  const handleExportErrorClose = () => {
    dispatch(clearExportError())
  }

  const handleExportingInfoClose = () => {
    dispatch(hideExportingInfo())
  }

  useEffect(() => {
    if (!data && !loading && error) {
      setOpenAlert(true)
    }
  }, [data, error, loading])

  return (
    <Box>
      <TabContext value={value}>
        <StyledTabBox>
          <StyledGrid container>
            {value !== `${Segment.segments.savedConstituentList.key}` && (
              <>
                <Grid item>
                  <StyledTabList onChange={handleChange}>
                    <StyledTab
                      label={`${Segment.segments.allProspects.label} (${totalCount.toLocaleString('en-US')})`}
                      value={Segment.segments.allProspects.key}
                    />
                    <StyledTab
                      label={`${Segment.segments.savedProspects.label}`}
                      value={Segment.segments.savedProspects.key}
                    />
                  </StyledTabList>
                </Grid>

                <StyledSnackbar
                  open={openAlert}
                  onClose={(event, reason) => {
                    if (reason === 'clickaway') return
                    handleAlertClose()
                  }}
                >
                  <StyledAlertSnackbar severity="error" onClose={handleAlertClose}>
                    Error occurred while saving user preferences
                  </StyledAlertSnackbar>
                </StyledSnackbar>

                <StyledSnackbar
                  open={exportError.showExportingInfo}
                  onClose={(event, reason) => {
                    if (reason === 'clickaway') return
                    handleExportingInfoClose()
                  }}
                >
                  <StyledAlertSnackbar severity="info" onClose={handleExportingInfoClose}>
                    Your file is being exported
                  </StyledAlertSnackbar>
                </StyledSnackbar>

                <StyledSnackbar
                  open={exportError.showError}
                  onClose={(event, reason) => {
                    if (reason === 'clickaway') return
                    handleExportErrorClose()
                  }}
                >
                  <StyledAlertSnackbar severity="error" onClose={handleExportErrorClose}>
                    {exportError.errorMessage}
                  </StyledAlertSnackbar>
                </StyledSnackbar>

                <StyledSnackbar
                  open={exportError.showSuccess}
                  onClose={(event, reason) => {
                    if (reason === 'clickaway') return
                    handleExportErrorClose()
                  }}
                >
                  <StyledAlertSnackbar severity="success" onClose={handleExportErrorClose}>
                    Your file has been successfully exported
                  </StyledAlertSnackbar>
                </StyledSnackbar>
              </>
            )}
          </StyledGrid>
        </StyledTabBox>
        <CustomTabPanel value={value}></CustomTabPanel>
      </TabContext>
    </Box>
  )
}

CustomTabPanel.propTypes = {
  value: PropTypes.string
}

export default ProspectSegmentation
